import { useCallback, useMemo } from 'react';

import { ClientSecretProvider } from '@stripe/stripe-react-native';
import { useClient } from 'urql';

import {
  CustomerSessionDocument,
  type CustomerSessionQuery,
  type CustomerSessionQueryVariables,
} from '../useCustomerSession/graphql/CustomerSession.generated';
import {
  CreateSetupIntentDocument,
  type CreateSetupIntentMutation,
  type CreateSetupIntentMutationVariables,
} from '../useSetupIntent/graphql/SetupIntent.generated';

/**
 * On native, the Stripe customer sheet uses a couple of provider functions to access the Stripe account.
 * Rather than providing a session id that was created once, this creates new ones whenever they are required.
 * This fixes an issue where reusing a session id after already using it to remove a payment method would crash the app.
 *
 * @see {@link https://docs.stripe.com/elements/customer-sheet?platform=react-native#react-native-present-wallet-element}
 */
export const useClientSecretProvider = (): ClientSecretProvider => {
  const client = useClient();

  const provideCustomerSessionClientSecret = useCallback(async () => {
    const query = client.query<CustomerSessionQuery, CustomerSessionQueryVariables>;

    const result = await query(
      CustomerSessionDocument,
      {},
      { requestPolicy: 'network-only' },
    ).toPromise();

    const nextCustomerId = result.data?.customerPaymentInfo?.id;
    const clientSecret = result.data?.customerPaymentInfo?.paymentSessionId;

    if (!nextCustomerId || !clientSecret) {
      throw result.error ?? new Error('Failed to fetch customer session');
    }

    return {
      customerId: nextCustomerId,
      clientSecret,
    };
  }, [client.query]);

  const provideSetupIntentClientSecret = useCallback(async () => {
    const mutation = client.mutation<CreateSetupIntentMutation, CreateSetupIntentMutationVariables>;

    const result = await mutation(CreateSetupIntentDocument, {}).toPromise();
    const secret = result.data?.createPaymentIntent;

    if (!secret) {
      throw result.error ?? new Error('Failed to create setup intent');
    }

    return secret;
  }, [client.mutation]);

  return useMemo(
    () => ({
      provideCustomerSessionClientSecret,
      provideSetupIntentClientSecret,
    }),
    [provideCustomerSessionClientSecret, provideSetupIntentClientSecret],
  );
};
